import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Maximize2, X } from 'lucide-react'
import type { BlockDef } from '@cogo/types-v2'
import {
  admitAgentSurfaceCogoJson,
  type CogoSurfaceProposal,
} from '@cogo/a2a-gateway/agent-surface-contract'
import type { GeneratedApplicationProposal } from '@cogo/a2a-gateway/generated-application-proposal'
import { requiredPreviewLayoutWidthPx } from './preview-layout-width'
import { useChatCardsCopy } from './chat-cards-copy'
import { BlockHost } from '@/shell/lib/block-host'

/**
 * How much of a previewed Cogo application may be driven.
 *
 * Three values, because there are three independent facts and the first two
 * used to be conflated:
 *
 *  · `agent-surface-preview` — CLOSED vocabulary. The JSON was authored by an
 *    untrusted QuickJS guest, so it is re-admitted through
 *    `admitAgentSurfaceCogoJson` before anything renders, and refused when it
 *    does not fit the six-type node grammar.
 *  · `generated-interactive` — OPEN vocabulary, reachable. The platform
 *    generated this with the user's own bearer and it already cleared the
 *    generated kind's own floor; re-admitting it through the guest sandbox
 *    would refuse the real Cogo vocabulary it is written in.
 *  · `disabled` — OPEN vocabulary, read-only.
 *
 * Before this split there was no way to say the middle one. Both drawing sites
 * mapped an interactive proposal to `agent-surface-preview`, which was
 * harmless only because the generated kind pinned `interactive: false` and the
 * branch could never run.
 */
export type CogoJsonPreviewInteractionMode =
  | 'disabled'
  | 'agent-surface-preview'
  | 'generated-interactive'

/**
 * The ONE mapping from a proposal to a preview mode.
 *
 * Exported because two cards draw these proposals, and a ternary written twice
 * is two places for the answer to differ. The discriminator is the generated
 * kind's own `interactive` field: `cogoSurfaceProposalSchema` is `.strict()`
 * and declares no such key, so its absence IS "this is an agent surface".
 */
export function previewInteractionModeFor(
  proposal: CogoSurfaceProposal | GeneratedApplicationProposal,
): CogoJsonPreviewInteractionMode {
  // Typed as the union rather than as `{ interactive?: boolean }` so the
  // compiler checks the discriminator too: a weak optional-only parameter
  // accepted any object at all, including one that had drifted the field away.
  if (!('interactive' in proposal)) return 'agent-surface-preview'
  return proposal.interactive ? 'generated-interactive' : 'disabled'
}

interface CogoJsonPreviewProps {
  json: string // Raw JSON string from code block
  interactionMode?: CogoJsonPreviewInteractionMode
}

const MAX_ELEMENTS = 100

function countElements(node: unknown): number {
  if (!node || typeof node !== 'object') return 0
  let count = 1
  const children = (node as Record<string, unknown>).children
  if (Array.isArray(children)) {
    for (const child of children) count += countElements(child)
  }
  return count
}

function isNonEmptyPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype &&
    Object.keys(value).length > 0
  )
}

type ParseResult =
  | { valid: true; data: BlockDef; error: null }
  | { valid: false; data: null; error: string }

/**
 * The application itself, laid out at the width its own markup needs.
 *
 * ONE component for both places it is drawn — the rail card and the
 * full-width view — because those two differ only in how much room they have,
 * never in how the application is rendered or in how much of it may be driven.
 */
function RenderedApplication({
  block,
  interactionMode,
  widthPx,
  fillAvailableWidth = false,
  transform,
  elementRef,
}: {
  block: BlockDef
  interactionMode: CogoJsonPreviewInteractionMode
  widthPx: number
  fillAvailableWidth?: boolean
  transform?: string
  elementRef?: React.Ref<HTMLDivElement>
}) {
  return (
    <div
      ref={elementRef}
      data-testid="cogo-json-rendered"
      // Read-only is the CSS, and it is the only thing the old
      // `interactive: false` pin ever bought: `useBlockHost` builds an
      // ActionFlowEngine for every block it hosts and its
      // `dispatchEvent` runs flows without consulting the mode at all.
      className={
        interactionMode === 'disabled' ? 'pointer-events-none' : undefined
      }
      style={{
        // The rail renders at the app's required layout width and scales that
        // fixed canvas down. The portalled reader has different authority: it
        // owns the available dialog width, so the same application must reflow
        // to that containing block while retaining the required width as a
        // floor when the window itself is narrower.
        width: fillAvailableWidth ? '100%' : `${widthPx}px`,
        ...(fillAvailableWidth ? { minWidth: `${widthPx}px` } : {}),
        ...(transform ? { transform, transformOrigin: 'top left' } : {}),
      }}
    >
      <BlockHost
        block={block}
        // Only the CLOSED vocabulary asks BlockHost to re-admit. A
        // generated block sent down that path is refused inside
        // `useBlockHost` before it can draw.
        interactionMode={
          interactionMode === 'agent-surface-preview'
            ? 'agent-surface-preview'
            : 'standard'
        }
      />
    </div>
  )
}

export function CogoJsonPreview({
  json,
  interactionMode = 'disabled',
}: CogoJsonPreviewProps) {
  const copy = useChatCardsCopy().appPreview
  const [showRaw, setShowRaw] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const parsed: ParseResult = useMemo(() => {
    try {
      const data = JSON.parse(json)
      // Generic previews still require a real root object before BlockHost.
      // Agent Surface mode then applies the stricter shared Gateway contract.
      if (!isNonEmptyPlainObject(data?.ui?.root)) {
        return { valid: false, data: null, error: copy.notPreviewable }
      }
      let previewData = data
      if (interactionMode === 'agent-surface-preview') {
        try {
          previewData = admitAgentSurfaceCogoJson(data)
        } catch {
          return { valid: false, data: null, error: copy.notPreviewable }
        }
      }
      const elementCount = countElements(previewData.ui.root)
      if (elementCount > MAX_ELEMENTS) {
        return {
          valid: false,
          data: null,
          error: copy.tooLarge(elementCount, MAX_ELEMENTS),
        }
      }
      // Deep clone to avoid Immer frozen state issues
      return {
        valid: true,
        data: JSON.parse(JSON.stringify(previewData)) as BlockDef,
        error: null,
      }
    } catch {
      return { valid: false, data: null, error: copy.unreadable }
    }
  }, [json, copy, interactionMode])

  /**
   * The width this application has to be laid out at, read off the
   * application. See `preview-layout-width.ts` for why this is a requirement
   * and not a floor — a floor of 375px was measured and does not reflow a
   * four-stat row, because that row is four columns at every width.
   */
  const layoutWidthPx = useMemo(
    () => requiredPreviewLayoutWidthPx(parsed.data),
    [parsed.data],
  )

  // The rail is narrower than any application worth previewing, so the card
  // shows the WHOLE screen scaled down rather than a squashed slice of it.
  // Scaling keeps the layout true; reading it is what the full-width view is
  // for. `availableWidth` is 0 until something measures it (and stays 0 where
  // there is no layout at all, e.g. jsdom), and 0 means "do not scale".
  const measureRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [availableWidth, setAvailableWidth] = useState(0)
  const [naturalHeight, setNaturalHeight] = useState(0)

  useLayoutEffect(() => {
    if (showRaw || typeof ResizeObserver === 'undefined') return
    const measure = measureRef.current
    const content = contentRef.current
    if (!measure || !content) return
    const read = () => {
      setAvailableWidth(measure.clientWidth)
      setNaturalHeight(content.scrollHeight)
    }
    const observer = new ResizeObserver(read)
    observer.observe(measure)
    observer.observe(content)
    read()
    return () => observer.disconnect()
  }, [showRaw, layoutWidthPx, json])

  const scale =
    availableWidth > 0 && availableWidth < layoutWidthPx
      ? availableWidth / layoutWidthPx
      : 1

  useEffect(() => {
    if (!expanded) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [expanded])

  if (!parsed.valid || !parsed.data) {
    return (
      <div
        className="my-2 p-3 rounded-md border border-destructive/30 bg-destructive/5 text-sm"
        data-testid="cogo-json-error"
      >
        <p className="text-destructive font-medium">{copy.errorTitle}</p>
        <p className="text-muted-foreground text-xs mt-1">{parsed.error}</p>
        <pre className="mt-2 text-xs overflow-x-auto">
          <code>{json}</code>
        </pre>
      </div>
    )
  }

  const name = parsed.data.meta?.name ?? copy.fallbackName

  return (
    <div className="my-2 rounded-md border bg-card" data-testid="cogo-json-preview">
      <div className="flex items-center justify-between gap-1 px-3 py-1.5 border-b bg-muted/50">
        <span className="min-w-0 truncate text-xs font-medium text-muted-foreground">
          {name}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <button
            className="text-xs px-2 py-0.5 rounded hover:bg-accent"
            onClick={() => setShowRaw((v) => !v)}
            data-testid="cogo-json-toggle"
          >
            {showRaw ? copy.showPreview : copy.showSource}
          </button>
          {/* The rail cannot be as wide as a generated application needs, so
              there has to be somewhere it CAN be read. Same block, same
              renderer, same interaction mode — only the room differs. */}
          <button
            type="button"
            className="flex items-center gap-1 rounded px-2 py-0.5 text-xs hover:bg-accent"
            onClick={() => setExpanded(true)}
            data-testid="cogo-json-expand"
            aria-label={copy.openFullWidth}
            title={copy.openFullWidth}
          >
            <Maximize2 className="h-3 w-3" aria-hidden />
          </button>
        </div>
      </div>
      <div className="p-2 overflow-auto max-h-[400px]">
        {showRaw ? (
          <pre className="text-xs overflow-x-auto" data-testid="cogo-json-raw">
            <code>{JSON.stringify(parsed.data, null, 2)}</code>
          </pre>
        ) : (
          <div ref={measureRef} className="w-full">
            <div
              style={
                scale === 1
                  ? undefined
                  : {
                      width: `${layoutWidthPx * scale}px`,
                      ...(naturalHeight > 0
                        ? { height: `${naturalHeight * scale}px` }
                        : {}),
                    }
              }
            >
              <RenderedApplication
                block={parsed.data}
                interactionMode={interactionMode}
                widthPx={layoutWidthPx}
                transform={scale === 1 ? undefined : `scale(${scale})`}
                elementRef={contentRef}
              />
            </div>
          </div>
        )}
      </div>

      {/* PORTALLED to the document, deliberately. `position: fixed` is relative
          to the nearest ancestor that has a transform, and the chat rail is
          `animate-in slide-in-from-right` + `overflow-hidden` — an animation
          whose fill-mode leaves a transform standing would make the rail the
          containing block and then clip the full-width view to the width this
          view exists to escape. */}
      {expanded &&
        typeof document !== 'undefined' &&
        createPortal(
        <div
          data-testid="cogo-json-expanded"
          role="dialog"
          aria-modal="true"
          aria-label={name}
          className="fixed inset-0 z-50 flex flex-col bg-background/95 p-4 backdrop-blur-sm"
          onKeyDown={(event) => {
            if (event.key === 'Escape') setExpanded(false)
          }}
        >
          <div className="flex shrink-0 items-center justify-between gap-2 pb-2">
            <span className="min-w-0 truncate text-sm font-medium">{name}</span>
            <button
              type="button"
              autoFocus
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-accent"
              onClick={() => setExpanded(false)}
              data-testid="cogo-json-collapse"
            >
              <X className="h-3 w-3" aria-hidden />
              {copy.close}
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            <RenderedApplication
              block={parsed.data}
              interactionMode={interactionMode}
              widthPx={layoutWidthPx}
              fillAvailableWidth
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
